import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { WidgetActivityGroupCountsDocument } from "#app/lib/data";
// Direct module path (not the dashboards barrel — barrel → registry → definitions cycle).
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import type { Omittable } from "@salestrak/ui";

type UseActivityGroupCountsOptions = Omittable<{
  /** Server grouping token, e.g. `"stage.entered"` or `"actor"`. */
  groupBy: string;
  /** The scoped activity `where` from `useScopedActivityWhere` — the caller owns the fold. */
  where: Record<string, unknown> | undefined;
  skip?: boolean;
}>;

type UseActivityGroupCountsResult = Omittable<{
  counts: Record<string, number>;
  ready: boolean;
  /** The query error, if any — a widget shows it as a `WidgetError` state. */
  error?: ErrorLike;
}>;

/**
 * Exact server-side activity counts grouped by a server token. The activity
 * fold (`useScopedActivityWhere`) narrows by team + actor + date; a workflow
 * dashboard additionally folds its pinned entity through `issueWhere` (activities
 * don't carry a workflow column — the server joins through the issue).
 */
export function useActivityGroupCounts({
  groupBy,
  where,
  skip = false,
}: UseActivityGroupCountsOptions): UseActivityGroupCountsResult {
  const scope = useDashboardScope();
  const issueWhere = scope?.entity?.type === "workflow" ? scope.entityWhere : undefined;

  const variables = useMemo(
    () => ({ groupBy, where, issueWhere }),
    [groupBy, where, issueWhere],
  );

  const { data, error } = useQuery(WidgetActivityGroupCountsDocument, {
    variables,
    skip,
    fetchPolicy: "cache-and-network",
  });

  const counts = useMemo(() => {
    const record: Record<string, number> = {};
    if (data?.activityGroupCounts) {
      for (const { key, total } of data.activityGroupCounts) {
        record[key] = total;
      }
    }
    return record;
  }, [data]);

  return { counts, ready: data?.activityGroupCounts !== undefined, error };
}
